import React, { useEffect, useId, useRef, useState } from 'react';

import { Eyebrow } from './parts';
import useShownOnce from '../../../hooks/useShownOnce';
import { joinMastersWaitlist, markMastersWaitlistSeen } from '../../../services/api';
import { windowShape } from '../../../services/astraLite';
import { track, EVENTS } from '../../../services/analytics';
import { MASTERS_ABROAD } from '../../../config/regions';
import {
    CONSENT_VERSION, FEATURED_PROGRAMS, MASTERS_COPY as COPY, PROGRAMS, moreFieldsLabel,
} from '../../../content/mastersWaitlist';

// Where the form runs, as the server names it (masters_waitlist.SURFACES).
export const SURFACE_LANDING = 'landing';
export const SURFACE_PAGE = 'masters_page';

// The landing page's Master's abroad section (#4967, epic #4962, ADR 0059):
// India visitors keep the US-undergrad page and also see this band, with a
// waitlist for the program pages. The page decides whether it renders
// (mastersSectionVisible, below); this component only runs the form.
//
// Measured: the section's view and a stored sign-up are counted on the
// server (masters-waitlist-seen / -join); the browser adds only where the
// form stops people (started, submit failed). Never the address.

// One mailbox, the server's rule (masters_waitlist.EMAIL_RE).
const ATOM = '[^\\s@,;:<>()\\[\\]\\\\"\']';
const EMAIL_RE = new RegExp(`^${ATOM}+@${ATOM}+\\.${ATOM}{2,}$`);
const INPUT = 'w-full min-h-[48px] rounded-[10px] border border-[#C9C6BD] bg-white px-3 py-2.5 text-[16px] text-[#2C2C2C] focus:border-[#1A4D2E] focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/30';
const INPUT_BAD = 'border-[#B42318]';
const LABEL = 'mb-1.5 block text-sm font-semibold text-[#2C2C2C]';
const HELP = 'mt-1 text-[13px] text-[#5F5F5F]';
const BTN = 'min-h-[52px] w-full rounded-xl bg-[#1A4D2E] px-6 text-base font-semibold text-white hover:bg-[#2D6B45] disabled:opacity-60 sm:w-auto';

/** True when the section belongs on this page view: the flag is on for this
 *  visitor AND the visitor's region strategy shows it. Pure, for tests. */
export function mastersSectionVisible({ flagOn, region }) {
    return Boolean(flagOn) && Boolean(region?.showsSection?.(MASTERS_ABROAD));
}

/** The page's own checks, mirroring the server's, as {field: line}. */
export function checkWaitlist({ email, programs, consent }, signedInEmail) {
    const errors = {};
    const addr = String(signedInEmail || email || '').trim();
    if (!addr || addr.length > 254 || !EMAIL_RE.test(addr)) errors.email = COPY.errors.email;
    if (!programs.length) errors.programs = COPY.errors.programs;
    if (!consent) errors.consent = COPY.errors.consent;
    return errors;
}

function FieldError({ id, children }) {
    if (!children) return null;
    // Announced when it appears (design spec #4985, section 6).
    return <p id={id} role="alert" className="mt-1 text-sm font-semibold text-[#B42318]">{children}</p>;
}

/** The platform's reach, from the landing stats the proof strip shows
 *  (live and thresholded server-side): only the figures that are served,
 *  never a typed number. Pure, for tests. */
export function reachFigures(stats) {
    if (stats?.live !== true) return [];
    const tiles = Array.isArray(stats.tiles) ? stats.tiles : [];
    const tile = (k) => tiles.find((t) => t?.key === k && t?.value);
    const picked = ['college_pages', 'video_views']
        .map((k) => tile(k))
        .filter(Boolean)
        .map((t) => ({ key: t.key, value: t.value, label: COPY.reachLabels[t.key] }));
    const r = stats.reach;
    const world = r && typeof r === 'object' && r.countries && r.world
        ? [{ key: 'countries', value: r.countries, label: COPY.reachLabels.countries }] : [];
    return [...picked, ...world];
}

export default function MastersWaitlist({ region, regionSource, signedInEmail = null, stats = null }) {
    const reach = reachFigures(stats);
    const ref = useRef(null);

    useShownOnce(ref, true, () => {
        markMastersWaitlistSeen({ region: region.code, region_source: regionSource, surface: SURFACE_LANDING, ...windowShape() });
    });

    return (
        <section ref={ref} id="masters-abroad" data-landing-section="masters_abroad" data-testid="landing-masters-abroad"
            className="scroll-mt-20 bg-[#F3F6F1] px-4 py-10 sm:px-6 lg:px-8 lg:py-16">
            <div className="mx-auto grid max-w-5xl gap-6 lg:grid-cols-2 lg:gap-10">
                <div className="min-w-0">
                    <Eyebrow>{COPY.eyebrow}</Eyebrow>
                    <h2 className="[text-wrap:balance] font-serif text-3xl font-bold text-[#1A4D2E] sm:text-4xl">{COPY.title}</h2>
                    <p className="mt-4 text-base leading-relaxed text-[#4A4A4A] sm:text-lg">{COPY.platform}</p>
                    <ReachFigures reach={reach} />
                    <p className="mt-5 text-base font-semibold leading-relaxed text-[#2C2C2C]">{COPY.body}</p>
                    <PointList points={COPY.points} testId="masters-points" />
                    <p className="mt-4 text-sm leading-relaxed text-[#6B6B6B]">{COPY.trust}</p>
                </div>
                <div className="min-w-0 rounded-2xl border border-[#E0DED8] bg-white p-4 sm:p-6">
                    <MastersWaitlistForm region={region} regionSource={regionSource} signedInEmail={signedInEmail}
                        surface={SURFACE_LANDING} />
                </div>
            </div>
        </section>
    );
}

/** The platform's reach figures, as a row of three; nothing when none is served. */
export function ReachFigures({ reach }) {
    if (!reach.length) return null;
    return (
        <ul className="mt-4 grid list-none grid-cols-3 gap-3 p-0" data-testid="masters-reach">
            {reach.map((f) => (
                <li key={f.key}>
                    <span className="block font-serif text-2xl font-bold text-[#1A4D2E] sm:text-3xl" style={{ fontVariantNumeric: 'tabular-nums' }}>{f.value}</span>
                    <span className="mt-1 block text-[13px] leading-snug text-[#6B6B6B]">{f.label}</span>
                </li>
            ))}
        </ul>
    );
}

/** A bulleted list of points. */
export function PointList({ points, testId }) {
    return (
        <ul className="mt-3 space-y-2 text-[15px] leading-relaxed text-[#4A4A4A]" data-testid={testId}>
            {points.map((pt) => (
                <li key={pt} className="flex gap-2">
                    <span aria-hidden="true" className="mt-[9px] h-1.5 w-1.5 flex-none rounded-full bg-[#1A4D2E]" />
                    <span>{pt}</span>
                </li>
            ))}
        </ul>
    );
}

/**
 * The waitlist form, one for both surfaces (#4985): the India home-page
 * section (`landing`) and /masters (`masters_page`). Same endpoint, same
 * checks; `surface` tells the server where the sign-up came from.
 * `compact` (/masters, mock A): the four featured fields two by two, the
 * rest behind "+10 more fields", so the button is on a phone's first screen.
 */
export function MastersWaitlistForm({ region, regionSource, signedInEmail = null, surface, compact = false }) {
    const id = useId();
    const [allFields, setAllFields] = useState(!compact);
    const shownPrograms = allFields ? PROGRAMS : PROGRAMS.filter((p) => FEATURED_PROGRAMS.includes(p.value));
    const hiddenCount = PROGRAMS.length - shownPrograms.length;
    // "Show 10 more programs" hands focus to the first checkbox it revealed,
    // so a keyboard or screen-reader visitor lands on the new choices.
    const boxes = useRef({});
    const focusAfterReveal = useRef(null);
    useEffect(() => {
        if (!allFields || !focusAfterReveal.current) return;
        boxes.current[focusAfterReveal.current]?.focus();
        focusAfterReveal.current = null;
    }, [allFields]);
    const revealAll = () => {
        focusAfterReveal.current = PROGRAMS.find((p) => !FEATURED_PROGRAMS.includes(p.value))?.value || null;
        setAllFields(true);
    };
    const started = useRef(false);
    const [email, setEmail] = useState('');
    const [programs, setPrograms] = useState([]);
    const [consent, setConsent] = useState(false);
    const [errors, setErrors] = useState({});
    const [formError, setFormError] = useState('');
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(null);

    const start = () => {
        if (started.current) return;
        started.current = true;
        track(EVENTS.MASTERS_WAITLIST_STARTED, { surface });
    };
    const toggle = (value) => {
        start();
        setPrograms((cur) => (cur.includes(value) ? cur.filter((p) => p !== value) : [...cur, value]));
        setErrors((e) => ({ ...e, programs: undefined }));
    };

    const submit = async (ev) => {
        ev.preventDefault();
        start();
        setFormError('');
        const errs = checkWaitlist({ email, programs, consent }, signedInEmail);
        if (Object.keys(errs).length) {
            setErrors(errs);
            track(EVENTS.MASTERS_WAITLIST_SUBMIT_FAILED, { error: Object.keys(errs)[0], surface });
            return;
        }
        setSending(true);
        const res = await joinMastersWaitlist({
            // A signed-in account's own address rides along too: the server
            // takes the verified token's address first, and uses this one
            // only for an account whose address Firebase has not verified.
            email: signedInEmail || email.trim(),
            programs: PROGRAMS.map((p) => p.value).filter((v) => programs.includes(v)),
            consent: CONSENT_VERSION,
            region: region.code,
            region_source: regionSource,
            surface,
            ...windowShape(),
        });
        setSending(false);
        if (res.ok) {
            setDone({ email: signedInEmail || email.trim(), rejoin: Boolean(res.rejoin) });
            return;
        }
        const field = ['email', 'programs', 'consent'].includes(res.field) ? res.field : null;
        track(EVENTS.MASTERS_WAITLIST_SUBMIT_FAILED, { error: field || res.error || 'other', surface });
        if (field) setErrors({ [field]: COPY.errors[field] });
        else setFormError(COPY.errors[res.error] || COPY.errors.broke);
    };

    const describe = (...ids) => ids.filter(Boolean).join(' ') || undefined;
    const programsLegend = `${id}-programs`;

    return (
        <>
                {done ? (
                    <p role="status" data-testid="masters-waitlist-done" className="text-base font-semibold text-[#1A4D2E]">
                        {done.rejoin ? COPY.rejoined : COPY.joined(done.email)}
                    </p>
                ) : (
                    <form noValidate onSubmit={submit} data-testid="masters-waitlist-form">
                        <fieldset aria-describedby={describe(`${id}-programs-h`, errors.programs && `${id}-programs-e`)}>
                            <legend id={programsLegend} className={LABEL}>{COPY.programsLabel}</legend>
                            <p id={`${id}-programs-h`} className={HELP}>{COPY.programsHelp}</p>
                            <div className={`mt-2 grid gap-1 ${compact ? 'grid-cols-2 gap-x-3 text-[15px] leading-snug' : 'sm:grid-cols-2'}`}>
                                {shownPrograms.map((p) => (
                                    <label key={p.value} className={`flex min-h-[48px] cursor-pointer items-center text-[#2C2C2C] ${compact ? 'gap-2' : 'gap-3'}`}>
                                        <input type="checkbox" checked={programs.includes(p.value)} onChange={() => toggle(p.value)}
                                            ref={(el) => { boxes.current[p.value] = el; }}
                                            className="h-6 w-6 flex-none accent-[#1A4D2E]" data-testid={`masters-program-${p.value}`} />
                                        <span>{p.label}</span>
                                    </label>
                                ))}
                            </div>
                            {hiddenCount > 0 && (
                                <button type="button" onClick={revealAll} data-testid="masters-more-fields"
                                    className="mt-1 min-h-[44px] text-sm font-semibold text-[#1A4D2E] underline decoration-[#CFE3D4] underline-offset-4">
                                    {moreFieldsLabel(hiddenCount)}
                                </button>
                            )}
                            <FieldError id={`${id}-programs-e`}>{errors.programs}</FieldError>
                        </fieldset>
                        <div className="mt-4">
                            {signedInEmail ? (
                                <p className="text-sm text-[#4A4A4A]" data-testid="masters-signed-in">{COPY.signedInAs(signedInEmail)}</p>
                            ) : (
                                <>
                                    <label htmlFor={`${id}-email`} className={LABEL}>{COPY.emailLabel}</label>
                                    <input id={`${id}-email`} type="email" autoComplete="email" required value={email}
                                        onChange={(e) => { start(); setEmail(e.target.value); setErrors((x) => ({ ...x, email: undefined })); }}
                                        aria-invalid={errors.email ? true : undefined}
                                        aria-describedby={describe(errors.email && `${id}-email-e`)}
                                        className={`${INPUT} ${errors.email ? INPUT_BAD : ''}`} />
                                    <FieldError id={`${id}-email-e`}>{errors.email}</FieldError>
                                </>
                            )}
                        </div>
                        <div className="mt-4">
                            <label className="flex min-h-[48px] cursor-pointer items-start gap-3">
                                <input type="checkbox" required checked={consent}
                                    onChange={(e) => { start(); setConsent(e.target.checked); setErrors((x) => ({ ...x, consent: undefined })); }}
                                    aria-invalid={errors.consent ? true : undefined}
                                    aria-describedby={describe(errors.consent && `${id}-consent-e`)}
                                    className="mt-0.5 h-6 w-6 flex-none accent-[#1A4D2E]" data-testid="masters-consent" />
                                <span className="text-sm text-[#2C2C2C]">{COPY.consent}</span>
                            </label>
                            <FieldError id={`${id}-consent-e`}>{errors.consent}</FieldError>
                        </div>
                        <div aria-live="polite">
                            {formError && <p role="alert" className="mt-3 text-sm font-semibold text-[#B42318]">{formError}</p>}
                        </div>
                        <button type="submit" disabled={sending} data-cta="masters_waitlist_join" className={`${BTN} mt-4`}>
                            {sending ? COPY.submitting : COPY.submit}
                        </button>
                    </form>
                )}
        </>
    );
}
